import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { CURRENCY_ID, normalizeCurrencyCode } from "../../../features/ValueFormater";
import { setFeatureData } from "../../store/slices/appSlice";
import AiAssistant from "../../Components/AiAssistant";
import DataTable from "../../Components/Table";
import AppButton from "../../Components/Common/Button";
import BalanceCardsGrid from "../../Components/AccountSummary/BalanceCardsGrid";
import {
  ACCOUNT_SUMMARY_QUICK_ACTIONS,
} from "../../Components/GetStartedActions/actions";
import TableActionButton from "../../Components/Common/TableActionButton";
import ApproveRejectPayroll from "../../Components/Payroll/ApproveRejectPayroll";
import ApproveRejectTransaction from "../../Components/PendingApprovals/ApproveRejectTransaction";
import TransactionDetailsPanel from "../../Components/TransactionDetailsPanel";

import useNewOverviewAPI from "../../api/overviewAPI";
import StatementOfAccountAPI from "../../../api/StatementofAccountsAPI";
import useNewTransactionDetailsAPI from "../../api/transactionDetailsAPI";
import useDetailsPanelAnchor from "../../hooks/useDetailsPanelAnchor";

import "../../Styles/pages/overview.scss";

const assistantAllowedEntityIds = new Set([
  "120",
  "47",
  "140",
  "49",
  "141",
  "50086",
  "66",
  "1604",
  "3575",
  "7015",
  "21732",
]);

const QUICK_ACTION_BUTTONS = [
  {
    title: "Pay Payroll",
    description: "Process employee salaries",
    className: "is-gold",
    path: "/payroll",
    src: "Images/PayPayrollLogo.svg",
  },
   {
    title: "Free Corporate Card",
    description: "Request a physical card",
    className: "is-violet",
    path: "/cardssettings",
    src: "Images/PaySuppLogo.png",
  },
  {
    title: "Butler Service",
    description: "Cash pickup and delivery",
    className: "is-blue",
    path: "/butlerservice",
    src: "Images/CashButLogo.png",
  },
  {
    title: "International Transfer",
    description: "Send money abroad",
    className: "is-blue",
    path: "/transferabroad",
    src: "Images/ITLogo.png",
  },
 
  
];

function getAmountCurrencyLabel(row, currencyId) {
  const original = row?.original || row;
  const normalizedCurrencyId = Number(currencyId || 0);

  if (normalizedCurrencyId === 1) {
    return "$";
  }

  if (normalizedCurrencyId === 2) {
    return "L.L";
  }

  return (
    row?.externalTransferCurrency ||
    original?.ExternalTransferCurrency ||
    row?.currency ||
    original?.Currency ||
    ""
  );
}

function formatOverviewAmount(
  amountValue,
  currencyId,
  row
) {
  const amount = Number(amountValue || 0);

  const currencyLabel =
    getAmountCurrencyLabel(
      row,
      currencyId
    );

  const formattedAmount =
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits:
        currencyLabel === "L.L" ? 0 : 2,
      maximumFractionDigits:
        currencyLabel === "L.L" ? 0 : 2,
    }).format(Math.abs(amount));

  if (currencyLabel === "$") {
    return `-$ ${formattedAmount}`;
  }

  return `-${currencyLabel ? `${currencyLabel} ` : ""}${formattedAmount}`;
}

function getTransactionCurrency(row) {
  const original = row?.original || row;

  const currencyId = Number(
    row?.currencyId ??
      original?.CurrencyId ??
      original?.CurrencyID ??
      0
  );

  if (currencyId === 1) {
    return "USD";
  }

  if (currencyId === 2) {
    return "LBP";
  }

  return (
    row?.currency ||
    original?.Currency ||
    original?.CurrencyCode ||
    row?.externalTransferCurrency ||
    original?.ExternalTransferCurrency ||
    ""
  );
}

function getTransactionAccount(row) {
  const original = row?.original || row;

  return (
    original?.Account ||
    original?.AccountName ||
    row?.account ||
    row?.WalletID ||
    original?.WalletID ||
    "-"
  );
}

function getTransactionMethod(row) {
  const original = row?.original || row;

  return (
    row?.method ||
    original?.Method ||
    original?.TransactionType ||
    original?.Type ||
    ""
  );
}

function TransactionTypeCell({ row }) {
  const method = getTransactionMethod(row);

  if (method === "S2S") {
    return (
      <div className="transaction-type-cell">
        <img
          src="/Images/S2SLogo.png"
          alt="ss"
          className="transaction-type-cell__icon"
        />
        <span>Suyool to Suyool</span>
      </div>
    );
  }

  if (method === "Payroll") {
    return (
      <div className="transaction-type-cell">
        <img
          src="/Images/PayPayrollLogo.svg"
          alt=""
          className="transaction-type-cell__icon"
        />
        <span>Payroll Run</span>
      </div>
    );
  }

  if (method === "InternationalTransfer") {
    return (
      <div className="transaction-type-cell">
        <img
          src="/Images/ITLogo.png"
          alt=""
          className="transaction-type-cell__icon"
        />
        <span>International Transfer</span>
      </div>
    );
  }

  return <span>{row?.name || row?.type || "Activity"}</span>;
}

function RecentTransactionTypeCell({ row }) {
  const original = row?.original || row;

  const description = String(
    original?.Description ||
      original?.description ||
      ""
  ).toLowerCase();

  if (description.includes("exchanged")) {
    return <span>Currency Exchange</span>;
  }

  if (description.includes("vat")) {
    return <span>VAT</span>;
  }

  if (description.includes("income tax")) {
    return <span>Income Tax</span>;
  }

  if (description.includes("eod closing")) {
    return <span>EOD Closing</span>;
  }

  if (original?.IsPayrollReference) {
    return <span>Payroll</span>;
  }

  return (
    <span>
      {row?.name || row?.type || "Activity"}
    </span>
  );
}

function AccountCell({ row }) {
  const account = getTransactionAccount(row);
  const currency = getTransactionCurrency(row);

  return (
    <span className="account__currency">
      {account} {currency}
    </span>
  );
}

function OverviewAmountCell({ value, row }) {
  const original = row?.original || row;

  const currencyId = Number(
    row?.currencyId ??
      original?.CurrencyId ??
      original?.CurrencyID ??
      0
  );

  return (
    <span className="pending__aprovals__amount">
      {formatOverviewAmount(
        value,
        currencyId,
        row
      )}
    </span>
  );
}

function SOAAccountCell({ row }) {
  const original = row?.original || row;

  return (
    <span className="account__currency">
      {original?.Account || "-"}
    </span>
  );
}

function SOATypeCell({ row }) {
  const original = row?.original || row;

  return (
    <span className="type_recent_transaction">
      {original?.Type || "-"}
    </span>
  );
}

function SOAAmountCell({ row }) {
  const original = row?.original || row;

  const amount = Number(
    original?.Amount || 0
  );

  const currencyId = Number(
    original?.CurrencyId ||
      original?.CurrencyID ||
      0
  );

  const sign =
    original?.Sign === "-"
      ? "-"
      : original?.Sign === "+"
      ? "+"
      : "";

  const currency =
    currencyId === 1
      ? "$"
      : currencyId === 2
      ? "L.L"
      : "";

  const formattedAmount =
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits:
        currency === "L.L" ? 0 : 2,
      maximumFractionDigits:
        currency === "L.L" ? 0 : 2,
    }).format(Math.abs(amount));

  const amountClass =
    sign === "+"
      ? "soa-amount--positive"
      : sign === "-"
      ? "soa-amount--negative"
      : "";

  return (
    <span className={amountClass}>
      {sign}
      {currency
        ? `${currency} ${formattedAmount}`
        : formattedAmount}
    </span>
  );
}

function SOAStatusCell({
  value,
  isUtcDateValue,
  renderTableCellValue,
}) {
  const status = String(value || "");

  if (!status) {
    return <span>-</span>;
  }

  if (isUtcDateValue(status)) {
    return (
      <span className="date__table">
        {renderTableCellValue(status)}
      </span>
    );
  }

  return <span>{status}</span>;
}

function RecentTransactionArrowCell() {
  return (
    <span className="recent-transactions__row-arrow">
      <FontAwesomeIcon icon={faChevronRight} />
    </span>
  );
}

export default function LegalOverviewPage({
  onOpenQrAfterGenerate,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    getOverviewTransactions,
    getOverviewBalances,
    transactions,
    balanceSections,
    bankAccountStatus,
    totalCount,
    loading,
    balancesLoading,
    error,
  } = useNewOverviewAPI();

  const {
    GetSOAList,
    GetStatementData,
    totalCount: soaTotalCount,
    soaLoading,
  } = StatementOfAccountAPI();

  const {
    GetTransactionDetails,
    resetTransactionDetails,
    transactionDetails,
    detailsLoading,
    detailsError,
  } = useNewTransactionDetailsAPI();

  const getOverviewTransactionsRef =
    useRef(getOverviewTransactions);

  const getOverviewBalancesRef =
    useRef(getOverviewBalances);

  const GetSOAListRef =
    useRef(GetSOAList);

  const GetTransactionDetailsRef =
    useRef(GetTransactionDetails);

  const resetTransactionDetailsRef =
    useRef(resetTransactionDetails);

  useEffect(() => {
    getOverviewTransactionsRef.current =
      getOverviewTransactions;
  }, []);

  useEffect(() => {
    getOverviewBalancesRef.current =
      getOverviewBalances;
  }, []);

  useEffect(() => {
    GetSOAListRef.current =
      GetSOAList;
  }, []);

  useEffect(() => {
    GetTransactionDetailsRef.current =
      GetTransactionDetails;
  }, []);

  useEffect(() => {
    resetTransactionDetailsRef.current =
      resetTransactionDetails;
  }, [resetTransactionDetails]);

  const [
    assistantPrompt,
    setAssistantPrompt,
  ] = useState("");

  const [
    activeTransactionKey,
    setActiveTransactionKey,
  ] = useState(null);

  const {
    anchorStyle,
    anchorToRow,
    containerRef,
    resetAnchor,
  } = useDetailsPanelAnchor();

  const [
    activeRecentTransactionKey,
    setActiveRecentTransactionKey,
  ] = useState(null);

  const {
    anchorStyle: recentAnchorStyle,
    anchorToRow: anchorRecentToRow,
    containerRef: recentContainerRef,
    resetAnchor: resetRecentAnchor,
  } = useDetailsPanelAnchor();

  const authorizationStatus =
    useSelector(
      (state) =>
        state.authorization?.status
    );

  const responseFlag =
    useSelector(
      (state) =>
        state.transferResponse?.flag
    );

  const shouldShowAssistant =
    assistantAllowedEntityIds.has(
      sessionStorage.getItem(
        "EntityID"
      ) || ""
    );

  const handleOpenAccount =
  useCallback(
    (card) => {
      if (!card?.currency) {
        return;
      }

      const accountType =
        String(card?.bankId || "")
          .toLowerCase() === "bsl"
          ? 2
          : 1;

      const currency = normalizeCurrencyCode(card.currency || "");
      const currencyId = CURRENCY_ID[currency];
      const accountNumber = accountType === 2
        ? String(card.accountNumber ?? "").trim()
        : "";

      if (!currencyId) {
        return;
      }

      dispatch(
        setFeatureData({
          section: "details",
          key: "selectedStatementAccount",
          value: {
            accountType,
            currencyId,
            accountNumber,
          },
        })
      );

      navigate(
        `/statementofaccount?accountType=${accountType}&currencyId=${currencyId}`,
        { state: { accountNumber } },
      );
    },
    [
      dispatch,
      navigate,
    ]
  );

  const handleOpenTransactionHistory =
    useCallback(() => {
      navigate(
        "/transactionhistory"
      );
    }, [navigate]);

  const handleQuickActionNavigation =
    useCallback(
      (action) => {
        if (action?.path) {
          navigate(action.path);
        }
      },
      [navigate]
    );

  useEffect(() => {
    let cancelled = false;

    const loadOverview = async () => {
      try {
        await Promise.all([
          getOverviewTransactionsRef.current(),
          getOverviewBalancesRef.current(),
        ]);
      } catch (requestError) {
        if (!cancelled) {
          console.error(
            "Failed to load overview:",
            requestError
          );
        }
      }
    };

    loadOverview();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadRecentTransactions =
      async () => {
        try {
          const today = new Date();

          const dateTo =
            `${today.getFullYear()}-${String(
              today.getMonth() + 1
            ).padStart(2, "0")}-${String(
              today.getDate()
            ).padStart(2, "0")}`;

          const previousMonth =
            new Date(today);

          previousMonth.setDate(
            previousMonth.getDate() -
              31
          );

          const dateFrom =
            `${previousMonth.getFullYear()}-${String(
              previousMonth.getMonth() + 1
            ).padStart(2, "0")}-${String(
              previousMonth.getDate()
            ).padStart(2, "0")}`;

          await GetSOAListRef.current(
            1,
            1,
            dateFrom,
            dateTo,
            "All", 
            0
          );

          if (cancelled) {
            return;
          }
        } catch (requestError) {
          if (!cancelled) {
            console.error(
              "Failed to load SOA transactions:",
              requestError
            );
          }
        }
      };

    loadRecentTransactions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleIbanStatusUpdated =
      () => {
        getOverviewBalancesRef.current().catch(
          () => {}
        );
      };

    window.addEventListener(
      "new:iban-status-updated",
      handleIbanStatusUpdated
    );

    return () => {
      window.removeEventListener(
        "new:iban-status-updated",
        handleIbanStatusUpdated
      );
    };
  }, []);

  const getExecutionRowKey =
    useCallback(
      (row, rowIndex) =>
        row?.id ||
        row?.reference ||
        row?.Transaction ||
        row?.transactionId ||
        row?.date ||
        rowIndex,
      []
    );

  const getRecentTransactionRowKey =
    useCallback(
      (row, rowIndex) =>
        row?.Transaction ||
        row?.id ||
        row?.Row ||
        rowIndex,
      []
    );

  const getDetailsTransactionID =
    useCallback((row) => {
      const original =
        row?.original || row;

      const rawId = String(
        original?.Transaction ||
          original?.TransactionID ||
          original?.Transfer ||
          row?.reference ||
          row?.id ||
          ""
      );

      if (!rawId) {
        return "";
      }

      if (
        original?.IsBankAccountTransfer ===
        2
      ) {
        return `rm${rawId}`;
      }

      if (
        original?.IsBankAccountTransfer ===
        1
      ) {
        return rawId.startsWith("ts")
          ? rawId
          : `ts${rawId}`;
      }

      return rawId;
    }, []);

  const handleOpenTransactionDetails =
    useCallback(
      async (
        row,
        rowIndex,
        event
      ) => {
        const transactionID =
          getDetailsTransactionID(
            row
          );

        if (!transactionID) {
          return;
        }

        anchorToRow(event);

        const original =
          row?.original || row;

        const requestID =
          original?.IsPayrollReference
            ? `${transactionID}-3`
            : transactionID;

        const additionalInfo =
          row?.typeId === 25
            ? row?.externalTransferTypeItem ||
              original?.eExternalTransferTypeItem ||
              ""
            : "";

        setActiveTransactionKey(
          getExecutionRowKey(
            row,
            rowIndex
          )
        );

        setActiveRecentTransactionKey(
          null
        );

        resetRecentAnchor();

        await GetTransactionDetailsRef.current(
          requestID,
          additionalInfo,
          row?.status ?? row?.Status ?? ""
        ).catch(() => {});
      },
      [
        anchorToRow,
        getDetailsTransactionID,
        getExecutionRowKey,
        resetRecentAnchor,
      ]
    );

  const handleOpenRecentTransactionDetails =
    useCallback(
      async (
        row,
        rowIndex,
        event
      ) => {
        const original =
          row?.original || row;

        const transactionID =
          String(
            original?.Transaction ||
              original?.TransactionID ||
              original?.Transfer ||
              original?.id ||
              ""
          );

        if (!transactionID) {
          return;
        }

        anchorRecentToRow(event);

        setActiveRecentTransactionKey(
          getRecentTransactionRowKey(
            original,
            rowIndex
          )
        );

        setActiveTransactionKey(
          null
        );

        resetAnchor();

        const requestID =
          original?.IsPayrollReference
            ? `${transactionID}-3`
            : transactionID;

        const additionalInfo =
          original?.typeId === 25
            ? original?.externalTransferTypeItem ||
              original?.eExternalTransferTypeItem ||
              ""
            : "";

        await GetTransactionDetailsRef.current(
          requestID,
          additionalInfo
        ).catch(() => {});
      },
      [
        anchorRecentToRow,
        getRecentTransactionRowKey,
        resetAnchor,
      ]
    );

  const handleCloseTransactionDetails =
    useCallback(() => {
      resetAnchor();

      setActiveTransactionKey(
        null
      );

      resetTransactionDetailsRef.current();
    }, [resetAnchor]);

  const handleCloseRecentTransactionDetails =
    useCallback(() => {
      resetRecentAnchor();

      setActiveRecentTransactionKey(
        null
      );

      resetTransactionDetailsRef.current();
    }, [resetRecentAnchor]);

  useEffect(() => {
    if (
      authorizationStatus ===
        "Complete" ||
      [0, 1].includes(responseFlag)
    ) {
      resetAnchor();
      resetRecentAnchor();

      setActiveTransactionKey(
        null
      );

      setActiveRecentTransactionKey(
        null
      );

      resetTransactionDetailsRef.current();

      getOverviewTransactionsRef.current().catch(
        () => {}
      );
    }
  }, [
    authorizationStatus,
    responseFlag,
    resetAnchor,
    resetRecentAnchor,
  ]);

  const pendingApprovalTransactions =
    useMemo(
      () =>
        Array.isArray(
          transactions
        )
          ? transactions.slice(
              0,
              6
            )
          : [],
      [transactions]
    );
    
  const recentTransactions =
    useMemo(() => {
      if (
        !Array.isArray(
          GetStatementData
        )
      ) {
        return [];
      }

      return GetStatementData.slice(
        0,
        10
      ).map(
        (item, index) => ({
          id:
            item?.Transaction ??
            item?.Row ??
            index,

          Transaction:
            item?.Transaction ??
            "-",

          date:
            item?.PaymentDate ||
            item?.RowDate ||
            null,

          WalletID:
            item?.WalletID ??
            0,

            Account: item?.Account || "-",

          Description:
            item?.Description ||
            "-",

          Amount:
            item?.Amount ??
            0,

          Balance:
            item?.Balance ??
            0,

          HeldBalance:
            item?.HeldBalance ??
            0,

          status:
            item?.Status ||
            "-",

          Sign:
            item?.Sign ||
            "",

          CurrencyId:
            item?.CurrencyId ??
            item?.CurrencyID ??
            0,

          IsPayrollReference:
            Boolean(
              item?.IsPayrollReference
            ),

          IsBankAccountTransfer:
            item?.IsBankAccountTransfer,

          Type:
            item?.Type ||
            "-",

          TransactionType:
            item?.TransactionType ||
            "",

          Row:
            item?.Row ??
            index + 1,
        })
      );
    }, [GetStatementData]);

  const pendingApprovalColumns =
    useMemo(
      () => [
        {
          header:
            "Transaction Type",
          accessor: "type",

          cell: ({ row }) => (
            <TransactionTypeCell
              row={row}

            />
          ),
        },

        {
          header: "Account",
          accessor: "account",

          cell: ({ row }) => (
            <AccountCell
              row={row}
            />
          ),
        },
        {
          header: "Account",
          accessor: "account",

          cell: ({ row }) => (
            <AccountCell
              row={row}
            />
          ),
        },

        {
          header: "Amount",
          accessor: "amount",

          cell: ({
            value,
            row,
          }) => (
            <OverviewAmountCell
              value={value}
              row={row}
            />
          ),
        },

        {
          header: "Date",
          accessor: "date",
        },

        {
          header:
            "Description",
          accessor:
            "description",

          cell: ({ row }) => (
            <span className="table_content_description">
              {row?.name ||
                row?.type ||
                "Activity"}
            </span>
          ),
        },

        {
          id: "actions",
          header: "Actions",

          cell: ({
            row,
            rowIndex,
          }) => {
            const original =
              row?.original || {};

            const transactionId =
              original?.Transaction ||
              row?.reference ||
              row?.id;

            const hideApprovalActions =
              original?.IsBankAccountTransfer ===
                3 ||
              original?.IsBankAccountTransfer ===
                2;

            return (
              <div className="new-overview-page__row-actions">
                <TableActionButton
                  label="View"
                  onClick={(
                    event
                  ) =>
                    handleOpenTransactionDetails(
                      row,
                      rowIndex,
                      event
                    )
                  }
                />

                {!hideApprovalActions &&
                original?.IsPayrollReference ? (
                  <ApproveRejectPayroll
                    payrollId={
                      transactionId
                    }
                    onOpenQrAfterGenerate={
                      onOpenQrAfterGenerate
                    }
                  />
                ) : null}

                {!hideApprovalActions &&
                !original?.IsPayrollReference ? (
                  <ApproveRejectTransaction
                    transactionId={
                      transactionId
                    }
                    method={
                      row?.method
                    }
                    account={
                      original?.Account
                    }
                    onOpenQrAfterGenerate={
                      onOpenQrAfterGenerate
                    }
                  />
                ) : null}
              </div>
            );
          },
        },
      ],
      [
        handleOpenTransactionDetails,
        onOpenQrAfterGenerate,
      ]
    );

  const recentTransactionColumns =
    useMemo(
      () => [
        {
          header:
            "Transaction ID",
          accessor:
            "Transaction",

          cell: ({ row }) => {
            const original =
              row?.original ||
              row;

            return (
              <span className="transactions__transactionid">
                {original?.Transaction ||
                  "-"}
              </span>
            );
          },
        },

        {
          header: "Date",
          accessor: "date",
        },

        {
          header: "Account",
          accessor: "WalletID",

          cell: ({ row }) => (
            <SOAAccountCell
              row={row}
            />
          ),
        },

        {
          header: "Type",
          accessor: "Type",

          cell: ({ row }) => (
            <SOATypeCell
              row={row}
            />
          ),
        },

        {
          header: "Details",
          accessor:
            "Description",

          cell: ({ row }) => {
            const original =
              row?.original ||
              row;

            return (
              <span className="table_content_description">
                {original?.Description ||
                  "-"}
              </span>
            );
          },
        },

        {
          header: "Amount",
          accessor: "Amount",

          cell: ({ row }) => (
            <SOAAmountCell
              row={row}
            />
          ),
        },

        {
          header: "Status",
          accessor: "status",

          cell: ({
            value,
            isUtcDateValue,
            renderTableCellValue,
          }) => {
            const status =
              String(
                value || ""
              );

            if (
              status.toUpperCase() ===
              "REVERSED"
            ) {
              return (
                <span className="soa-status soa-status--reversed">
                  Reversed
                </span>
              );
            }

            return (
              <SOAStatusCell
                value={status}
                isUtcDateValue={
                  isUtcDateValue
                }
                renderTableCellValue={
                  renderTableCellValue
                }
              />
            );
          },
        },

        {
          id: "recentActions",
          header: "",

          cell: () => (
            <span className="recent-transactions__row-arrow">
              <FontAwesomeIcon
                icon={
                  faChevronRight
                }
              />
            </span>
          ),
        },
      ],
      []
    );

  const activeTransaction =
    useMemo(
      () =>
        Array.isArray(
          transactions
        )
          ? transactions.find(
              (
                row,
                rowIndex
              ) =>
                getExecutionRowKey(
                  row,
                  rowIndex
                ) ===
                activeTransactionKey
            )
          : null,
      [
        transactions,
        activeTransactionKey,
        getExecutionRowKey,
      ]
    );

  const isActiveExternalTransfer =
    Number(
      activeTransaction?.typeId ||
        0
    ) === 25;

  return (
    <div className="new-overview-page">
      <div className="new-overview-page__header">
        <div>
          {/* <h1>
            Account Overview
          </h1>

          <p>
            Monitor balances,
            cash flow, and
            recent activity.
          </p> */}
        </div>
      </div>

      <BalanceCardsGrid
        sections={
          balanceSections
        }
        bankAccountStatus={
          bankAccountStatus
        }
        loading={
          balancesLoading
        }
        onCardClick={
          handleOpenAccount
        }
      />

      {shouldShowAssistant ? (
        <AiAssistant
          value={
            assistantPrompt
          }
          onChange={
            setAssistantPrompt
          }
          quickActions={
            ACCOUNT_SUMMARY_QUICK_ACTIONS
          }
          variant="account-summary"
          onQuickActionClick={
            handleQuickActionNavigation
          }
          onOpenQrAfterGenerate={
            onOpenQrAfterGenerate
          }
        />
      ) : (
        <div>
          <h2 className="quick__action__h2">
            Quick Actions
          </h2>

          <div
            className="assistant-quick-actions"
            aria-label="Quick Actions"
          >
            {QUICK_ACTION_BUTTONS.map(
              (action) => (
                <div
                  key={
                    action.title
                  }
                  className="assistant-quick-actions__grid"
                >
                  <button
                    type="button"
                    className={`assistant-quick-action-card ${action.className}`}
                    onClick={() =>
                      navigate(
                        action.path
                      )
                    }
                  >
                    <img
                      className="assistant-quick-action-card__icon"
                      aria-hidden="true"
                      src={
                        action.src
                      }
                      alt=""
                    />

                    <span className="assistant-quick-action-card__copy">
                      <strong>
                        {
                          action.title
                        }
                      </strong>

                      <small>
                        {
                          action.description
                        }
                      </small>
                    </span>

                    <span
                      className="assistant-quick-action-card__arrow"
                      aria-hidden="true"
                    >
                      <img
                        src="Images/RightArrowLogo.png"
                        alt=""
                      />
                    </span>
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* PENDING APPROVALS */}

      <section
        className={`new-overview-page__execution ${
          activeTransactionKey
            ? "is-details-open"
            : ""
        }`}
      >
        <div className="new-overview-page__execution-header">
          <h2>
            Pending Approvals
          </h2>

          <AppButton
            type="button"
            variant="outline"
            className="new-overview-page__view-all"
            onClick={() =>
              navigate(
                "/pendingapprovals"
              )
            }
          >
            View All
          </AppButton>
        </div>

        <div
          className="new-overview-page__execution-body"
          ref={
            containerRef
          }
        >
          <div className="new-overview-page__execution-table">
            <DataTable
              columns={
                pendingApprovalColumns
              }
              data={
                pendingApprovalTransactions
              }
              loading={
                loading
              }
              rowKey={
                getExecutionRowKey
              }
              rowClickable
              onRowClick={
                handleOpenTransactionDetails
              }
              activeRowKey={
                activeTransactionKey
              }
              emptyMessage="No execution activity available."
            />
          </div>

          {activeTransactionKey ? (
            <div
              className="new-overview-page__details-panel new-transaction-details-anchor"
              style={
                anchorStyle
              }
            >
              <TransactionDetailsPanel
                variant={
                  isActiveExternalTransfer
                    ? "transfer-abroad"
                    : "default"
                }
                details={
                  transactionDetails
                }
                error={
                  detailsError
                }
                loading={
                  detailsLoading
                }
                onClose={
                  handleCloseTransactionDetails
                }
                onOpenQrAfterGenerate={
                  onOpenQrAfterGenerate
                }
              />
            </div>
          ) : null}
        </div>

        <p className="new-overview-page__execution-note">
          {error
            ? "Unable to load recent activity right now"
            : `Recent automation runs across workflows and systems. Showing ${
                pendingApprovalTransactions.length
              } of ${
                totalCount ||
                pendingApprovalTransactions.length
              }.`}
        </p>
      </section>

      {/* RECENT TRANSACTIONS */}

      <section
        className={`new-overview-page__execution ${
          activeRecentTransactionKey
            ? "is-details-open"
            : ""
        }`}
      >
        <div className="new-overview-page__execution-header">
          <h2>
            Recent Transactions
          </h2>

          <AppButton
            type="button"
            variant="outline"
            className="new-overview-page__view-all"
            onClick={
              handleOpenTransactionHistory
            }
          >
            View All
          </AppButton>
        </div>

        <div
          className="new-overview-page__execution-body"
          ref={
            recentContainerRef
          }
        >
          <div className="new-overview-page__execution-table">
            <DataTable
              columns={
                recentTransactionColumns
              }
              data={
                recentTransactions
              }
              loading={
                soaLoading
              }
              rowKey={
                getRecentTransactionRowKey
              }
              rowClickable
              onRowClick={
                handleOpenRecentTransactionDetails
              }
              activeRowKey={
                activeRecentTransactionKey
              }
              emptyMessage="No recent transactions available."
            />
          </div>

          {activeRecentTransactionKey ? (
            <div
              className="new-overview-page__details-panel new-transaction-details-anchor"
              style={
                recentAnchorStyle
              }
            >
              <TransactionDetailsPanel
                variant="default"
                details={
                  transactionDetails
                }
                error={
                  detailsError
                }
                loading={
                  detailsLoading
                }
                onClose={
                  handleCloseRecentTransactionDetails
                }
                onOpenQrAfterGenerate={
                  onOpenQrAfterGenerate
                }
              />
            </div>
          ) : null}
        </div>

        <p className="new-overview-page__execution-note">
          {soaLoading
            ? "Loading recent transactions..."
            : `Recent transactions across your account. Showing ${
                recentTransactions.length
              } of ${
                soaTotalCount ||
                recentTransactions.length
              }.`}
        </p>
      </section>
    </div>
  );
}
